'use client';

import Text, { TextTag, TextVariant } from '@pixum/text';
import clsx from 'clsx';
import type { ChildCartArticleType } from 'services/types';
import type { LocaleIso } from 'utils';
import { useCartStore, type CartStore } from 'stores/cart';
import { getChildArticlePriceDetails, getLiveArticlePrice } from '../../../../utils';
import { CartPrice } from '../../../../../partials/CartPrice';
import styles from './ArticlePrice.module.css';

export interface ArticlePriceScale {
  /** Discount in percent compared to the price without graduated scale. */
  percentage: number;
  /** Formatted total without graduated scale — shown struck through. */
  originalPrice: string;
}

export interface ArticlePriceProps {
  articleId: number;
  price: string;
  childArticles: ChildCartArticleType[];
  locale: LocaleIso;
  className?: string;
  /** Only photo prints have graduated prices; other articles render the plain price. */
  isScalable: boolean;
  /** RSC/SSR graduated price info — replaced by the PSI-confirmed values after a quantity change. */
  scale?: ArticlePriceScale;
  /** Contentful label in front of the percentage, e.g. "You save". */
  scaleLabel?: string;
}

/**
 * Thin wrapper around CartPrice scoped to a specific article.
 * The .price CSS class provides layout (min-width, text-align, font).
 * CartPrice handles the skeleton when this article's quantity is mutating.
 * For photo prints the graduated price info (percentage + struck-through total) is shown below the price.
 */
export function ArticlePrice({
  articleId,
  price,
  childArticles,
  locale,
  className,
  isScalable,
  scale,
  scaleLabel
}: ArticlePriceProps) {
  const liveValues = useCartStore((s: CartStore) => s.cartLiveValues);
  const pendingArticle = useCartStore((s: CartStore) => s.pendingArticle);

  const liveDetailsPrice = childArticles.reduce((total, child) => {
    const childQuantity = liveValues?.childAmounts[child.id] ?? child.quantity;
    const { totalPriceChildArticle } = getChildArticlePriceDetails(child, childQuantity);

    return total + totalPriceChildArticle;
  }, 0);

  const { liveValue, scale: activeScale } = getLiveArticlePrice({
    price,
    scale,
    articlePrice: liveValues?.articlePrices[articleId],
    articlePriceScaled: liveValues?.articlePricesScaled[articleId],
    detailsPrice: liveDetailsPrice,
    isScalable,
    locale
  });

  if (!isScalable) {
    return (
      <CartPrice
        value={price}
        variant={TextVariant.HEADLINE}
        articleId={articleId}
        liveValue={liveValue}
        locale={locale}
        className={className}
      />
    );
  }

  const isPending = pendingArticle?.topic === 'quantity' && pendingArticle.articleId === articleId;
  const scaleInfo = [scaleLabel, activeScale && `${activeScale.percentage}%`].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <CartPrice
        value={price}
        variant={TextVariant.HEADLINE}
        articleId={articleId}
        liveValue={liveValue}
        locale={locale}
      />
      {activeScale && (
        <Text
          tag={TextTag.SPAN}
          variant={TextVariant.CAPTION}
          className={clsx(styles['scale'], isPending && styles['scale--pending'])}
        >
          <span>{scaleInfo}</span>
          <s>{activeScale.originalPrice}</s>
        </Text>
      )}
    </div>
  );
}
